(() => {
  'use strict';

  const params = new URLSearchParams(window.location.search);
  if (params.get('header_debug') !== '1') return;

  const MAX_SAMPLES = 240;
  const HEADER_SELECTOR = '.shopify-section-header';
  const COMPONENT_SELECTOR = 'header-component';
  const MAIN_SELECTOR = '#MainContent';
  const ATTRIBUTE_FILTER = ['class', 'aria-hidden', 'dynamic-sticky', 'style'];
  const startedAt = window.performance.now();
  const samples = [];
  const pendingReasons = new Set();
  let observer = null;
  let frameId = null;
  let stopped = false;
  let lastScrollY = window.scrollY;
  let lastVisualSignature = '';

  const rounded = (value) => Math.round(Number(value) * 100) / 100;

  const rectOf = (element) => {
    if (!element) return null;
    const rect = element.getBoundingClientRect();

    return {
      top: rounded(rect.top),
      right: rounded(rect.right),
      bottom: rounded(rect.bottom),
      left: rounded(rect.left),
      width: rounded(rect.width),
      height: rounded(rect.height),
    };
  };

  const styleOf = (element) => {
    if (!element) return null;
    const style = window.getComputedStyle(element);

    return {
      position: style.position,
      top: style.top,
      bottom: style.bottom,
      transform: style.transform,
      transition: style.transition,
      height: style.height,
      marginTop: style.marginTop,
      marginBottom: style.marginBottom,
      overflow: style.overflow,
      overflowX: style.overflowX,
      overflowY: style.overflowY,
      contain: style.contain,
      zIndex: style.zIndex,
    };
  };

  const describe = (element) => {
    if (!element) return null;

    return {
      tag: element.tagName.toLowerCase(),
      id: element.id || '',
      className: typeof element.className === 'string' ? element.className : '',
    };
  };

  const ancestryOf = (element) => {
    const ancestors = [];
    let current = element ? element.parentElement : null;

    while (current && ancestors.length < 8) {
      ancestors.push({
        element: describe(current),
        rect: rectOf(current),
        style: styleOf(current),
      });
      current = current.parentElement;
    }

    return ancestors;
  };

  const sectionState = (section) => section ? {
    element: describe(section),
    ariaHidden: section.getAttribute('aria-hidden'),
    dynamicSticky: section.hasAttribute('dynamic-sticky'),
    scrolled: section.classList.contains('scrolled'),
    visible: section.hasAttribute('visible'),
    rect: rectOf(section),
    style: styleOf(section),
  } : null;

  const componentState = (component) => component ? {
    element: describe(component),
    rect: rectOf(component),
    style: styleOf(component),
    headerSectionBottom: component.headerSectionBottom ?? null,
    lastScrollPosition: component.lastScrollPosition ?? null,
    isHidden: component.isHidden ?? null,
    isDynamicSticky: component.isDynamicSticky ?? null,
    isDynamicShowLocked: component.isDynamicShowLocked ?? null,
    isCurrentSticky: component.isCurrentSticky ?? null,
    lastBottom: component.lastBottom ?? null,
    lastHeight: component.lastHeight ?? null,
  } : null;

  const variablesOf = (section) => {
    const style = window.getComputedStyle(section || document.documentElement);

    return {
      headerHeight: style.getPropertyValue('--gsc-header-height').trim(),
      headerPaddingBlock: style.getPropertyValue('--gsc-header-padding-block').trim(),
      stickyBarBottom: style.getPropertyValue('--gsc-sticky-bar-bottom').trim(),
      asidePaddingSize: style.getPropertyValue('--gsc-aside-padding-size').trim(),
    };
  };

  const capture = (reason = 'manual') => {
    if (stopped) return null;

    const section = document.querySelector(HEADER_SELECTOR);
    const component = document.querySelector(COMPONENT_SELECTOR);
    const main = document.querySelector(MAIN_SELECTOR);
    const scrollY = rounded(window.scrollY);
    const direction = scrollY > lastScrollY ? 'down' : scrollY < lastScrollY ? 'up' : 'none';
    const serializedSection = sectionState(section);
    const serializedComponent = componentState(component);
    const sample = {
      elapsedMs: rounded(window.performance.now() - startedAt),
      reason,
      scrollY,
      direction,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
      },
      section: serializedSection,
      component: serializedComponent,
      main: { rect: rectOf(main), style: styleOf(main) },
      variables: variablesOf(section),
      ancestors: ancestryOf(section),
    };
    const visualSignature = JSON.stringify({
      direction,
      section: serializedSection ? {
        ariaHidden: serializedSection.ariaHidden,
        dynamicSticky: serializedSection.dynamicSticky,
        scrolled: serializedSection.scrolled,
        position: serializedSection.style.position,
        top: serializedSection.style.top,
        transform: serializedSection.style.transform,
      } : null,
      component: serializedComponent ? {
        isHidden: serializedComponent.isHidden,
        isDynamicSticky: serializedComponent.isDynamicSticky,
        lastScrollPosition: serializedComponent.lastScrollPosition,
        position: serializedComponent.style.position,
        top: serializedComponent.style.top,
        transform: serializedComponent.style.transform,
      } : null,
    });
    const previous = samples[samples.length - 1];
    const shouldStore = reason !== 'scroll' || !previous ||
      Math.abs(scrollY - previous.scrollY) >= 8 || visualSignature !== lastVisualSignature;

    lastScrollY = scrollY;
    lastVisualSignature = visualSignature;
    if (!shouldStore) return sample;

    samples.push(sample);
    if (samples.length > MAX_SAMPLES) samples.splice(0, samples.length - MAX_SAMPLES);
    return sample;
  };

  const schedule = (reason) => {
    if (stopped) return;

    pendingReasons.add(reason);
    if (frameId !== null) return;

    frameId = window.requestAnimationFrame(() => {
      frameId = null;
      const reasonText = [...pendingReasons].join('+');
      pendingReasons.clear();
      capture(reasonText);
    });
  };

  const handleScroll = () => schedule('scroll');
  const handleResize = () => schedule('resize');

  const getReport = () => ({
    version: 1,
    page: window.location.pathname,
    generatedAt: new Date().toISOString(),
    stopped,
    samples: JSON.parse(JSON.stringify(samples)),
  });

  const copy = async () => {
    const json = JSON.stringify(getReport(), null, 2);

    try {
      await navigator.clipboard.writeText(json);
      console.info('[headerStickyDebug] JSON copiado.');
    } catch (error) {
      console.warn('[headerStickyDebug] Não foi possível copiar; use getReport().', error);
    }

    return json;
  };

  const clear = () => {
    if (stopped) return;

    samples.splice(0, samples.length);
    lastVisualSignature = '';
    lastScrollY = window.scrollY;
    capture('clear');
  };

  const stop = () => {
    if (stopped) return;

    capture('stop');
    stopped = true;
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', handleResize);
    if (observer) observer.disconnect();
    if (frameId !== null) window.cancelAnimationFrame(frameId);
    pendingReasons.clear();
    console.info('[headerStickyDebug] Coleta interrompida.');
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  window.addEventListener('resize', handleResize);

  const section = document.querySelector(HEADER_SELECTOR);
  if (section) {
    observer = new MutationObserver((mutations) => {
      const attributes = mutations.map(({ attributeName }) => attributeName).filter(Boolean);
      schedule(`mutation:${[...new Set(attributes)].join(',')}`);
    });
    observer.observe(section, { attributes: true, attributeFilter: ATTRIBUTE_FILTER });
  }

  window.headerStickyDebug = { capture, clear, copy, getReport, stop };
  capture('initial');
  console.info('[headerStickyDebug] Ativo. Use await headerStickyDebug.copy() após reproduzir.');
})();
